/**
 * usePasswordReset Hook
 *
 * React Query mutations for password reset operations.
 */

import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { PasswordResetRequest, PasswordResetConfirm } from '../../../types/auth.types';
import { authService } from '../../../api/auth.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface PasswordResetResponse {
  message: string;
}

interface UsePasswordResetOptions {
  onSuccess?: (data: PasswordResetResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for requesting password reset email
 */
export const useRequestPasswordReset = (
  options?: UsePasswordResetOptions
): UseMutationResult<PasswordResetResponse, Error, PasswordResetRequest> => {
  return useMutation<PasswordResetResponse, Error, PasswordResetRequest>({
    mutationFn: async (data: PasswordResetRequest) => {
      return await authService.requestPasswordReset(data);
    },
    onSuccess: (data) => {
      console.log('[useRequestPasswordReset] Reset email sent successfully');
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useRequestPasswordReset] Request failed:', getErrorMessage(error));
      options?.onError?.(error);
    },
  });
};

/**
 * Hook for confirming password reset with token
 */
export const useConfirmPasswordReset = (
  options?: UsePasswordResetOptions
): UseMutationResult<PasswordResetResponse, Error, PasswordResetConfirm> => {
  return useMutation<PasswordResetResponse, Error, PasswordResetConfirm>({
    mutationFn: async (data: PasswordResetConfirm) => {
      return await authService.resetPassword(data);
    },
    onSuccess: (data) => {
      console.log('[useConfirmPasswordReset] Password reset successful');
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useConfirmPasswordReset] Reset failed:', getErrorMessage(error));
      options?.onError?.(error);
    },
  });
};
